import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DeleteButton from "@/components/DeleteButton";

const ROLES = ["worker", "supervisor", "admin"];

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", role: "worker", pin: "" });

  const load = () => api.get("/users").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.phone || !form.pin) { toast.error("All fields required"); return; }
    try {
      const payloadRole = user?.role === "supervisor" ? "worker" : form.role;
      await api.post("/auth/register", { name: form.name, phone: form.phone, role: payloadRole, pin: form.pin, password: form.pin });
      toast.success("User created"); setOpen(false); setForm({ name: "", phone: "", role: "worker", pin: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const roleBadge = (r) => r === "admin" ? "bg-[#1C1C1A] text-white" : r === "supervisor" ? "bg-[#5B7B61] text-white" : "bg-[#E8E8E6] text-[#1C1C1A]";

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Team</div>
            <h1 className="font-display text-4xl tracking-tighter font-black">Users</h1>
          </div>
          {(user?.role === "admin" || user?.role === "supervisor") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="add-user" className="bg-[#1C1C1A] hover:bg-black text-white"><Plus className="w-4 h-4 mr-2" /> Add worker</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add new {user?.role === "admin" ? "user" : "worker"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                  <div><Label>Phone (10 digits)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone" /></div>
                  {user?.role === "admin" && (
                    <div>
                      <Label>Role</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-2">{form.role}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent>{ROLES.map(r => <DropdownMenuItem key={r} onClick={() => setForm({ ...form, role: r })}>{r}</DropdownMenuItem>)}</DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  <div><Label>PIN / Password</Label><Input type="password" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} data-testid="user-pin" /></div>
                </div>
                <Button onClick={save} data-testid="user-save" className="mt-4 bg-[#1C1C1A] hover:bg-black text-white">Create</Button>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <Card className="mt-8 border border-[#D8D8D5] shadow-none rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Role</TableHead><TableHead>Joined</TableHead>{user?.role === "admin" && <TableHead>Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>{r.phone}</TableCell>
                  <TableCell><Badge className={roleBadge(r.role)}>{r.role}</Badge></TableCell>
                  <TableCell className="text-xs text-[#6E6E6A]">{(r.created_at || "").slice(0,10)}</TableCell>
                  {user?.role === "admin" && (
                    <TableCell>
                      {r.id !== user.id && <DeleteButton endpoint={`/users/${r.id}`} label={`user ${r.name}`} onDone={load} testid={`del-user-${r.id}`} />}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
